import {
  CubeDimensionEnum,
  FeatureEnum,
  ModelNamesBesidesFeatureEnums,
} from '../../services/data/impl/cube/cubeSlice';
import { priorityServicesOverVersion } from '../../services/data/impl/cube/cubeUtils';
import { compareModelVersions } from '../imageViewer/utils/imageUtils';
import { RanchPageViewEnum } from './ranchesSlice';

export const getRandomArbitrary = (min: number, max: number) =>
  Math.random() * (max - min) + min;

export const roundFractional = (num: number, maximumFractionDigits = 2) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits }).format(num);

export const acresToLinearFoots = (acres: number) => acres * 43560;

export const DEFAULT_NULL_VALUE = 'N/D';

interface ScanWithModelVersion {
  [CubeDimensionEnum.detectionFactScanId]: string;
  [CubeDimensionEnum.className]: string;
  [CubeDimensionEnum.modelName]: string;
  [CubeDimensionEnum.modelVersion]: string;
}

/**
 * Filters an array of objects with scanID, model name, and version by first grouping them by the detectionFactScanId.
 * Then, within each group, if multiple objects share the same className, it uses a helper function to first filter
 * out duplicates by prioritizing a specific model (e.g., "grape_early_mid_season") when duplicates are found.
 * Afterward, among the potentially reduced set of summaries, only the one with the highest modelVersion is kept.
 * The modelVersion comparison now considers versions that may contain both words and numbers, prioritizing
 * versions with the word and the highest number if present. Otherwise, the version with the highest number is used.
 *
 * @param customerSummaries - An array of objects which contains scanID, model name and version to filter.
 *
 * @returns A new array with the filtered objects.
 */
export function filterScansByModelVersion<T extends ScanWithModelVersion>(
  customerSummaries: T[]
): T[] {
  // First, group summaries by detectionFactScanId
  const scanIdToSummaries = new Map<string, T[]>();

  customerSummaries.forEach((summary) => {
    const scanIdGroup =
      scanIdToSummaries.get(summary[CubeDimensionEnum.detectionFactScanId]) ||
      [];
    scanIdGroup.push(summary);
    scanIdToSummaries.set(
      summary[CubeDimensionEnum.detectionFactScanId],
      scanIdGroup
    );
  });

  // For each scanId group, apply the highest model version logic
  let filteredSummaries: T[] = [];

  scanIdToSummaries.forEach((scanIdGroup) => {
    const filteredScanIdGroup = filterDuplicateClassNamesByServiceNames(
      scanIdGroup,
      priorityServicesOverVersion
    );

    // Group summaries in the scanIdGroup by className and keep only the one with the highest modelVersion in each group
    const classNameToSummary = new Map<string, T>();

    filteredScanIdGroup.forEach((summary) => {
      const existingSummary = classNameToSummary.get(
        summary[CubeDimensionEnum.className]
      );

      // If there is no existing summary for this className or if the current summary
      // has a higher modelVersion, store the current summary
      if (
        !existingSummary ||
        compareModelVersions(
          summary[CubeDimensionEnum.modelVersion],
          existingSummary[CubeDimensionEnum.modelVersion]
        ) > 0
      ) {
        classNameToSummary.set(summary[CubeDimensionEnum.className], summary);
        // if classes are same, but we have different models with same version - push
        // both models to the list of summaries
      } else if (
        summary[CubeDimensionEnum.className] ===
          existingSummary[CubeDimensionEnum.className] &&
        summary[CubeDimensionEnum.modelName] !==
          existingSummary[CubeDimensionEnum.modelName] &&
        compareModelVersions(
          summary[CubeDimensionEnum.modelVersion],
          existingSummary[CubeDimensionEnum.modelVersion]
        ) === 0
      ) {
        filteredSummaries.push(summary);
      }
    });

    // Add the filtered summaries in the scanIdGroup to the overall filtered summaries
    filteredSummaries = [
      ...filteredSummaries,
      ...Array.from(classNameToSummary.values()),
    ];
  });

  return filteredSummaries;
}

/**
 * Filters summaries to resolve duplicates of class names within the same scan group by prioritizing an array of service names.
 * This is useful for handling cases where certain models should be prioritized
 * when multiple summaries share the same class name (e.g., if services "bud" and "grape_early_mid_season" both have classNames "bud",
 * we can specify to use the services in priority order). This function only performs filtering if any service in the array
 * is found within the customerSummaries; otherwise, it returns the original summaries untouched.
 *
 * @param customerSummaries - The summaries to filter.
 * @param servicesToPrioritize - The array of model names to prioritize in order when duplicates are found.
 * @returns A filtered array of summaries where duplicates have been addressed by prioritizing the specified model names.
 */
function filterDuplicateClassNamesByServiceNames<
  T extends ScanWithModelVersion
>(
  customerSummaries: T[],
  servicesToPrioritize: (FeatureEnum | ModelNamesBesidesFeatureEnums)[]
): T[] {
  // Check if any of the servicesToPrioritize are found in the customerSummaries
  const serviceFound = servicesToPrioritize.some((service) =>
    customerSummaries.some(
      (summary) => summary[CubeDimensionEnum.modelName] === service
    )
  );

  // If none of the servicesToPrioritize are found, return the data untouched
  if (!serviceFound) {
    return customerSummaries;
  }

  const filteredSummaries: T[] = [];

  // Build a dictionary of class names and count of occurrences
  const classNameToCount = new Map<string, number>();
  customerSummaries.forEach((summary) => {
    const className = summary[CubeDimensionEnum.className];
    const count = classNameToCount.get(className) || 0;
    classNameToCount.set(className, count + 1);
  });

  // Iterate through the summaries and if the class name is a duplicate, only keep it if it's one of the prioritized services
  customerSummaries.forEach((summary) => {
    const className = summary[CubeDimensionEnum.className];
    const count = classNameToCount.get(className) || 0;

    if (count > 1) {
      for (const service of servicesToPrioritize) {
        if (summary[CubeDimensionEnum.modelName] === service) {
          filteredSummaries.push(summary);
          break;
        }
      }
    } else {
      filteredSummaries.push(summary);
    }
  });

  return filteredSummaries;
}

/**
 * Returns the appropriate color based on the provided active view.
 *
 * @param currentView - The currently active RanchPageViewEnum value.
 * @param buttonView - The expected RanchPageViewEnum value of the button.
 * @param theme - The current theme object which includes color definitions.
 * @return - The color corresponding to the provided active view. Returns bloomfieldGreen if the active view is the same as the expected button view, otherwise returns white.
 */
export function getActivePageViewColor(
  currentView: RanchPageViewEnum,
  buttonView: RanchPageViewEnum,
  theme: { colors: { common: { white: string; bloomfieldGreen: string } } }
): string {
  if (currentView === buttonView) {
    return theme.colors.common.bloomfieldGreen;
  } else {
    return theme.colors.common.white;
  }
}
